import type {
  PlanningUserAssignment,
  UserAssignmentRole,
  UserAssignmentScopeType,
} from '../../../../api/userAssignmentApis';

export type AssignUsersScope = UserAssignmentScopeType;

export type AssignUserOption = {
  value: string;
  label: string;
};

export type AssignLocationOption = {
  value: string;
  label: string;
  depth: number;
  /** Ancestor names only (excludes self), e.g. `Tower A › Floor 1`. */
  breadcrumb?: string;
};

export type AssignTaskOption = {
  id: string;
  label: string;
  locationId: string | null;
  /** Ancestor context (location path and/or parent tasks). */
  breadcrumb?: string;
};

/** Chip target in the Assign drawer. */
export type AssignTargetChip = {
  kind: 'location' | 'task';
  id: string;
  label: string;
  /** Always required by planning user-assignment API. */
  locationId: string;
  breadcrumb?: string;
  /**
   * Grid row ids that produced this chip (location-type rows collapse by locationId).
   * Used so Locations-tab remove/clear can uncheck the grid without reverse-lookup.
   */
  sourceTaskIds?: string[];
};

export const ASSIGN_ROLE_OPTIONS: { value: UserAssignmentRole; label: string }[] = [
  { value: 'planner', label: 'Planner' },
  { value: 'capturer', label: 'Capturer' },
  { value: 'reviewer', label: 'Reviewer' },
];

/** Soft role colors for dropdowns, avatars, and chips (light bg + readable fg). */
export const ASSIGN_ROLE_COLORS: Record<
  UserAssignmentRole,
  { bg: string; fg: string }
> = {
  planner: { bg: '#dbeafe', fg: '#1e40af' },
  capturer: { bg: '#ccfbf1', fg: '#115e59' },
  reviewer: { bg: '#ffedd5', fg: '#9a3412' },
};

const ASSIGN_ROLE_COLOR_FALLBACK = { bg: '#e2e8f0', fg: '#475569' };

export function assignRoleColor(
  role: string | null | undefined,
): { bg: string; fg: string } {
  const key = String(role ?? '').trim().toLowerCase();
  if (key === 'planner' || key === 'capturer' || key === 'reviewer') {
    return ASSIGN_ROLE_COLORS[key];
  }
  return ASSIGN_ROLE_COLOR_FALLBACK;
}

export type AssignUsersDrawerMode = 'mock' | 'live';

export type AssignPreview = {
  targetCount: number;
  userCount: number;
  role: UserAssignmentRole | null;
  willCreate: number;
  skippedNoLocation: number;
};

export function computeAssignPreview(params: {
  scope: AssignUsersScope;
  targets: AssignTargetChip[];
  userIds: string[];
  role: UserAssignmentRole | null;
}): AssignPreview {
  const validTargets = params.targets.filter((t) => t.locationId.trim() !== '');
  const skippedNoLocation = params.targets.length - validTargets.length;
  const userCount = params.userIds.length;
  const role = params.role;
  const willCreate =
    role && userCount > 0 ? validTargets.length * userCount : 0;
  return {
    targetCount: validTargets.length,
    userCount,
    role,
    willCreate,
    skippedNoLocation,
  };
}

/** Why Assign is disabled, or null when it can submit. */
export function computeAssignDisabledReason(params: {
  projectId: string;
  scope: AssignUsersScope;
  targets: AssignTargetChip[];
  userIds: string[];
  role: UserAssignmentRole | null;
  submitting?: boolean;
}): string | null {
  if (params.submitting) return 'Saving assignments…';
  if (!params.projectId.trim()) return 'No project is selected.';
  const validTargets = params.targets.filter((t) => t.locationId.trim() !== '');
  if (validTargets.length === 0) {
    if (params.targets.length > 0) {
      return 'All selected targets are missing a location and cannot be assigned.';
    }
    return params.scope === 'location'
      ? 'Add at least one location target.'
      : 'Add at least one task target.';
  }
  if (params.userIds.length === 0) return 'Select at least one user.';
  if (!params.role) return 'Select a role.';
  return null;
}

export type { PlanningUserAssignment, UserAssignmentRole };
